/**
 * Platform-Admin standalone login portal.
 *
 * Visual completamente distinto do CleanManager: tema dark/violeta com
 * branding "Platform Admin" para reforçar que é um produto separado.
 *
 * - Aceita apenas credenciais cujo /api/auth/login retorna is_super_admin=true.
 * - Se conta normal tentar logar aqui, faz logout imediato e exibe erro.
 * - Se um super-admin já autenticado abrir esta URL, redireciona direto
 *   para /super-admin/tenants.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, KeyRound, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthContext";

export default function PlatformLogin() {
  const { user, login, logout } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Se já está logado como super-admin, vai direto pro painel.
  useEffect(() => {
    if (user?.is_super_admin) nav("/super-admin/tenants", { replace: true });
  }, [user, nav]);

  async function onSubmit(e) {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await login(email.trim(), password);
      if (!u?.is_super_admin) {
        // conta normal — derruba a sessão e bloqueia
        await logout();
        setErr(
          "Esta credencial não tem acesso ao painel da plataforma. Use o login do CleanManager.",
        );
        return;
      }
      toast.success(`Bem-vindo(a), ${u.name}`);
      nav("/super-admin/tenants", { replace: true });
    } catch (e2) {
      setErr(e2.message || "Falha na autenticação");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{
        background:
          "radial-gradient(1200px 600px at 10% -10%, rgba(139,92,246,0.25), transparent), radial-gradient(900px 500px at 90% 110%, rgba(217,70,239,0.2), transparent), linear-gradient(180deg,#0b0b18 0%,#0a0a12 100%)",
      }}
      data-testid="platform-login-page"
    >
      {/* Voltar ao site principal */}
      <button
        onClick={() => nav("/login")}
        data-testid="platform-login-back"
        className="absolute top-4 left-4 inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white px-3 py-1.5 rounded-full border border-white/10 bg-white/5 backdrop-blur-sm"
      >
        <ArrowLeft size={12} /> CleanManager
      </button>

      <div className="w-full max-w-md relative">
        {/* glow */}
        <div
          className="absolute -inset-1 rounded-3xl blur-2xl opacity-40"
          style={{
            background:
              "linear-gradient(135deg, rgba(139,92,246,0.6), rgba(217,70,239,0.4))",
          }}
          aria-hidden
        />
        <div className="relative bg-slate-950/80 backdrop-blur-xl border border-white/10 rounded-3xl shadow-2xl p-8">
          <div className="flex items-center gap-3 mb-1">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{
                background:
                  "linear-gradient(135deg, #8b5cf6, #d946ef)",
                boxShadow: "0 6px 20px rgba(139,92,246,0.45)",
              }}
            >
              <Shield size={20} className="text-white" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-fuchsia-400 font-bold">
                Platform · Admin
              </div>
              <div className="text-xs text-slate-500">CleanManager SaaS</div>
            </div>
          </div>

          <h1 className="text-3xl font-bold text-white mt-5">
            Portal exclusivo<br />
            <span
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage: "linear-gradient(135deg, #c4b5fd, #f0abfc)",
              }}
            >
              do operador da plataforma.
            </span>
          </h1>
          <p className="text-sm text-slate-400 mt-2">
            Acesso restrito ao Super Admin. Aqui você gerencia empresas (tenants),
            preços de planos, menus e suporte cross-tenant.
          </p>

          <form onSubmit={onSubmit} className="mt-7 space-y-4">
            <Field label="E-mail do Super Admin">
              <input
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="platform-login-email"
                required
                placeholder="admin@empresa.com"
                className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/5 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-fuchsia-500/40 focus:border-fuchsia-500/50 transition"
              />
            </Field>
            <Field label="Senha">
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="platform-login-password"
                required
                placeholder="••••••••"
                className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/5 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-fuchsia-500/40 focus:border-fuchsia-500/50 transition"
              />
            </Field>

            {err && (
              <div
                data-testid="platform-login-error"
                className="text-sm rounded-lg border border-red-500/30 bg-red-500/10 text-red-200 px-3 py-2"
              >
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={busy || !email || !password}
              data-testid="platform-login-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold text-white disabled:opacity-50 transition"
              style={{
                background:
                  "linear-gradient(135deg, #8b5cf6 0%, #d946ef 100%)",
                boxShadow: "0 10px 30px rgba(139,92,246,0.35)",
              }}
            >
              {busy ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Autenticando…
                </>
              ) : (
                <>
                  <KeyRound size={16} /> Entrar na plataforma
                </>
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-white/5 text-[11px] text-slate-500 leading-relaxed">
            Este portal é independente do CleanManager. Se você é cliente, gestor
            ou colaborador,{" "}
            <button
              onClick={() => nav("/login")}
              className="text-fuchsia-300 hover:text-fuchsia-200 underline underline-offset-2"
            >
              use o login regular
            </button>
            .
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}
